import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ClientService from '../../../services/ClientService';
import AuthService from '../../../services/AuthService';

// Icônes et constantes
export const PDF_ICON_URL = 'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg';
export const WORD_ICON_URL = 'https://upload.wikimedia.org/wikipedia/commons/8/8d/Microsoft_Office_Word_%282019%E2%80%93present%29.svg';

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
export const ACCEPTED_DOC_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
export const ACCEPTED_IMAGE_ATTR = 'image/jpeg,image/jpg,image/png,image/webp';
export const ACCEPTED_DOC_ATTR = 'image/jpeg,image/jpg,image/png,image/webp,application/pdf';

export const extractErrorMessage = (err, fallback = "Une erreur est survenue.") => {
    return err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;
};

export const getFileType = (url) => {
    if (!url) return 'unknown';
    if (url === 'pdf') return 'pdf';
    if (url.startsWith('blob:')) return 'image';
    const ext = url.split('?')[0].split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    return 'unknown';
};

const identStyles = {
    sectionWrapper: { border: '1px solid #1976d233', borderRadius: '18px', padding: '28px', background: 'linear-gradient(160deg, #eef4ff 0%, #ffffff 55%)', marginTop: '8px', boxShadow: '0 4px 18px rgba(16,24,40,0.06)' },
    sectionTitle: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
    requiredBadge: { background: '#1976d2', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 13px', borderRadius: '20px' },
    infoAlert: { background: '#fffaf0', border: '1px solid #ffe2a8', borderRadius: '12px', padding: '13px 16px', display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '22px', color: '#7a5800', fontSize: '13.5px' },
    uploadZoneValid: { border: '1.5px solid #34a853', borderRadius: '14px', padding: '20px', background: '#f3faf4', boxShadow: '0 2px 10px rgba(52,168,83,0.14)' },
    uploadZone: { border: '1.5px dashed #1976d2', borderRadius: '14px', padding: '20px', background: '#fff' },
    missingIndicator: { color: '#1976d2', fontWeight: 600, fontSize: '12.5px', display: 'flex', alignItems: 'center', gap: '5px', background: '#1976d214', padding: '3px 10px', borderRadius: '999px' },
    presentIndicator: { color: '#1e7e34', fontWeight: 600, fontSize: '12.5px', display: 'flex', alignItems: 'center', gap: '5px', background: '#34a85318', padding: '3px 10px', borderRadius: '999px' },
    iconColor: '#1976d2', titleColor: '#1565c0', borderHex: '#1976d2'
};

export const useClientIdentification = () => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [clientId, setClientId] = useState(null);

    // Lightbox & DocModal
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSlides, setLightboxSlides] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [docModal, setDocModal] = useState({ open: false, url: '', type: '', label: '' });

    const [formData, setFormData] = useState({
        id: null,
        useRef: '',
        totalNumOfContracts: 0,
        identityDocumentType: 'CNI',
        lastModifiedBy: '',
        // Infos user (read-only)
        firstName: '', lastName: '', email: '', phoneNumber: '',
        neighborhood: '', city: '', country: '', dateOfBirth: '', gender: '',
        // Fichiers
        profilePhoto: null, profilePhotoPreview: null, profilePhotoUrl: '',
        identityDocument: null, identityDocumentPreview: null, identityDocumentUrl: '',
    });

    useEffect(() => {
        const fetchClient = async () => {
            try {
                setLoading(true);
                // Récupération de l'utilisateur depuis le localStorage
                const user = AuthService.getCurrentUser();
                if (!user || !user.id) throw new Error("Utilisateur non trouvé");

                setClientId(user.id);
                const response = await ClientService.getClientById(user.id);

                setFormData(prev => ({
                    ...prev,
                    id: response.id,
                    useRef: response.useRef || '',
                    totalNumOfContracts: response.totalNumOfContracts || 0,
                    identityDocumentType: response.identityDocumentType || 'CNI',
                    lastModifiedBy: response.user?.username || 'client',
                    // Infos user
                    firstName: response.user?.firstName || '',
                    lastName: response.user?.lastName || '',
                    email: response.user?.email || '',
                    phoneNumber: response.user?.phoneNumber || '',
                    neighborhood: response.user?.neighborhood || '',
                    city: response.user?.city || '',
                    country: response.user?.country || '',
                    dateOfBirth: response.user?.dateOfBirth || '',
                    gender: response.user?.gender || '',
                    // URLs pré-signées pour affichage
                    profilePhotoUrl: response.profilePhotoUrlPresigned || response.profilePhotoUrl || '',
                    identityDocumentUrl: response.identityDocumentUrlPresigned || response.identityDocumentUrl || '',
                }));
            } catch (err) {
                console.error(err);
                setError("Impossible de charger vos informations.");
            } finally {
                setLoading(false);
            }
        };
        fetchClient();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileUpload = (event, fieldName) => {
        const file = event.target.files[0];
        if (!file) return;

        const isImage = fieldName === 'profilePhoto';
        const acceptedTypes = isImage ? ACCEPTED_IMAGE_TYPES : ACCEPTED_DOC_TYPES;

        if (!acceptedTypes.includes(file.type)) {
            toast.error(isImage ? 'Format image requis (JPEG, PNG, WEBP).' : 'Format requis (Image, PDF).');
            return;
        }
        if (file.size > 10 * 1024 * 1024) { toast.error('Taille max: 10MB.'); return; }

        const previewField = `${fieldName}Preview`;
        const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : 'pdf';

        setFormData(prev => ({ ...prev, [fieldName]: file, [previewField]: preview }));
        event.target.value = '';
    };

    const handleRemoveFile = (fieldName) => {
        const previewField = `${fieldName}Preview`;
        const urlField = `${fieldName}Url`;
        setFormData(prev => ({ ...prev, [fieldName]: null, [previewField]: null, [urlField]: '' }));
    };

    const handleOpenPreview = (url, type, label = '') => {
        if (!url || url === 'pdf') return;
        if (type === 'image') {
            setLightboxSlides([{ src: url }]);
            setLightboxIndex(0);
            setLightboxOpen(true);
            return;
        }
        setDocModal({ open: true, url, type, label });
    };

    const handleCloseDocModal = () => setDocModal({ open: false, url: '', type: '', label: '' });
    const handleCloseLightbox = () => setLightboxOpen(false);

    const handleSave = async () => {
        try {
            setSaving(true); setError(null); setSuccess(null);

            // Construction du DTO attendu par le backend
            const clientData = {
                useRef: formData.useRef,
                totalNumOfContracts: formData.totalNumOfContracts,
                identityDocumentType: formData.identityDocumentType,
                lastModifiedBy: formData.lastModifiedBy
            };

            await ClientService.updateClientWithFiles(
                formData.id,
                clientData,
                formData.profilePhoto,
                formData.identityDocument
            );

            setSuccess("Vos informations ont été mises à jour avec succès.");
            toast.success("Mise à jour réussie !");
            setIsEditMode(false);

            // Recharger les données
            const updated = await ClientService.getClientById(formData.id);
            setFormData(prev => ({
                ...prev,
                profilePhotoUrl: updated.profilePhotoUrlPresigned || updated.profilePhotoUrl || '',
                identityDocumentUrl: updated.identityDocumentUrlPresigned || updated.identityDocumentUrl || '',
                profilePhoto: null, profilePhotoPreview: null,
                identityDocument: null, identityDocumentPreview: null,
            }));

        } catch (err) {
            console.error(err);
            const msg = extractErrorMessage(err, "Erreur lors de la sauvegarde.");
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditMode(false);
        setError(null); setSuccess(null);
        // Annuler les modifications de fichiers
        setFormData(prev => ({
            ...prev,
            profilePhoto: null, profilePhotoPreview: null,
            identityDocument: null, identityDocumentPreview: null,
        }));
    };

    return {
        isEditMode, setIsEditMode, loading, saving, error, success,
        lightboxOpen, lightboxSlides, lightboxIndex, setLightboxIndex, handleCloseLightbox,
        docModal, handleCloseDocModal,
        formData, setFormData,
        handleInputChange, handleFileUpload, handleRemoveFile,
        handleOpenPreview, handleSave, handleCancel, identStyles
    };
};